namespace Solovis.Reports.Parts.Tables.DataTableHelpers {

  const pixelToExcelWidthRatio = 7.1;
  const pixelToExcelHeightRatio = 1.333333333333333333333;
  const defaultExcelColumnWidthInPixels = 64;

  function internalSetExcelCellFromHtmlIfNonNull(excelCell, htmlContent) {
    if (typeof (htmlContent) !== "undefined" && htmlContent !== null) {
      if (typeof htmlContent === "string") {
        excelCell.value = $("<div>").html(htmlContent).text();
      }
      else if (htmlContent instanceof jQuery) {
        excelCell.value = (htmlContent as JQuery).text();
      }
      else if (typeof htmlContent === "object") {
        excelCell.value = $(htmlContent).text();
      }
    }
  };

  const standardExcelTableBorderColor = "FFDBDBDE";
  const standardExcelTableHeaderBgColor = "FFECF0F1";
  const standardExcelTableAggregateBgColor = "FFFEFECC";
  const standardExcelTableEvenBgColor = "FFFFFFFF";
  const standardExcelTableOddBgColor = "FFEEEEEE";

  function applyStandardBgColor(excelCell, isOdd) {
    excelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isOdd ? standardExcelTableOddBgColor : standardExcelTableEvenBgColor },
      bgColor: { argb: isOdd ? standardExcelTableOddBgColor : standardExcelTableEvenBgColor }
    };
  };


  function applyStandardHeaderBgColor(excelCell) {
    excelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: standardExcelTableHeaderBgColor },
      bgColor: { argb: standardExcelTableHeaderBgColor }
    };
  };

  function applyStandardCellBorder(excelCell) {
    excelCell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
      bottom: { style: "thin" },
      color: { argb: standardExcelTableBorderColor }
    };
  }

  function applyExcelCellIndent(excelCell, level) {
    if (level > 0) {
      excelCell.alignment = { indent: level * 2 };
    }
  }


  /**
   * ExcelWysiwygTableRenderer is responsible for converting the current view of a table to an Excel file that as closely matches 
   * what user sees on screen as we can manage.
   */
  export class ExcelWysiwygTableRenderer {

    private _excelWorkbook;
    private _sheetName;
    private _tableControl: DataTableControl;

    private _dataView;
    private _tableSettingsToUse;

    private _ws;
    private _nextRow;

    // State for image to add to worksheet.
    private _imageToAdd;

    constructor(options) {
      this._excelWorkbook = options.excelWorkbook;
      this._sheetName = options.sheetName ? options.sheetName : "Table";
      this._tableControl = options.tableControl;

      this._dataView = this._tableControl.dataView;
      this._tableSettingsToUse = this._tableControl._tableSettingsToUse;
    }


    private static _getAllowedWorksheetName(wb, proposedName: string): string {
      // 31 character max.
      // Alphanumeric or " ", ".", or "_".

      let name = "";
      if (proposedName) {
        for (let i = 0; i < proposedName.length; i++) {
          let ch = proposedName[i];
          if ((ch >= 'a' && ch <= 'z')
            || (ch >= 'A' && ch <= 'Z')
            || (ch >= '0' && ch <= '9')
            || (ch === " ")
            || (ch === ".")
            || (ch === "_")) {
            name += ch;
          }
          else {
            name += "_";
          }
        }
      }

      // We leave some room to add numbers at end of name.
      let maxLength = 27;
      if (name.length > maxLength) {
        name = name.substr(0, maxLength);
      }

      let nextNumber = 2;
      let candidateName = name;

      while (nextNumber < 100) {
        let nameApproved = true;

        wb.eachSheet(function (worksheet, sheetId) {
          if (worksheet.name === candidateName) {
            nameApproved = false;
          }
        });

        if (nameApproved) {
          break;
        }

        candidateName = name + "_" + nextNumber;
        nextNumber++;
      }

      return candidateName;
    }


    public static exportAllReportTablesToExcel(reportControl: Solovis.Reports.ReportControl) {
      Solovis.ModuleLoader.modalEnsureModuleListLoaded(["exceljs"], async () => {
        let ExcelJS = (window as any).ExcelJS;

        let modalWaitPanel = new Solovis.UI.ModalWaitPanel({
        });
        modalWaitPanel.show();

        let workbook = new ExcelJS.Workbook();

        var crawler = new Solovis.Reports.Util.SubReportCrawler({
          reportControl: reportControl,
          reportControlCallback: async (r: Solovis.Reports.ReportControl, crawlContext: Solovis.Reports.Util.SubReportCrawlContext) => {
            let defaultSheetName = null;

            if (crawlContext.labelPath && crawlContext.labelPath.length > 0) {
              // Use last portion of the label path as the sheet name.
              defaultSheetName = crawlContext.labelPath[crawlContext.labelPath.length - 1];
            }

            if (!defaultSheetName) {
              defaultSheetName = "Table";
            }

            let elementNames = r.getElementNameList();
            for (let elementName of elementNames) {
              let partControl = r.getReportPartControl(elementName);
              if (partControl instanceof DataTableControl) {
                let sheetNameToUse = ExcelWysiwygTableRenderer._getAllowedWorksheetName(workbook, defaultSheetName);

                let wysiwyg = new ExcelWysiwygTableRenderer({
                  excelWorkbook: workbook,
                  tableControl: partControl,
                  sheetName: sheetNameToUse
                });

                await wysiwyg.renderTableToSheetAsync();
              }
            }
          },

          allDoneCallback: () => {
            modalWaitPanel.hide();

            let tableName = "tables_" + kendo.toString(new Date(), "yyyy-MM-dd_HH-mm-ss") + ".xlsx";

            this.saveXlsx(workbook, tableName);
          }
        });

        try {
          await crawler.start();
        }
        catch (error) {
          console.log(error);

          Solovis.UI.alert("Exporting all tables To Excel failed (see JS console).");
        }
      });
    }


    public static exportTableToExcel(tableControl: DataTableControl) {
      Solovis.ModuleLoader.modalEnsureModuleListLoaded(["exceljs"], async () => {
        var modalPanel = new Solovis.UI.ModalWaitPanel({
        });
        modalPanel.show();

        try {
          let ExcelJS = (window as any).ExcelJS;

          let workbook = new ExcelJS.Workbook();

          let wysiwyg = new ExcelWysiwygTableRenderer({
            excelWorkbook: workbook,
            tableControl: tableControl,
            sheetName: "Table"
          });
          await wysiwyg.renderTableToSheetAsync();

          let pageViewModel = (window as any).PageViewModel;
          let reportName = pageViewModel.reportName !== undefined ? pageViewModel.reportName.toString().replace(/[^a-z0-9]+/gi, "") : "table";
          let tableName = reportName + "_" + kendo.toString(new Date(), "yyyy-MM-dd_HH-mm-ss") + ".xlsx";

          this.saveXlsx(workbook, tableName);
        }
        finally {
          modalPanel.dispose();
        }
      });
    }

    private static saveXlsx(wb: any, downloadFilename: string) {
      wb.xlsx.writeBuffer({
        base64: true
      })
        .then(function (xls64) {
          // NOTE: Tested in Chrome and IE11.                    
          var data = new Blob([xls64], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

          (window as any).saveAs(data, downloadFilename, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        })
        .catch(function (error) {
          console.log(error.message);
        });
    }


    public async renderTableToSheetAsync() {
      this._ws = this._excelWorkbook.addWorksheet(this._sheetName);
      this._nextRow = 1;

      let imageToAdd: any = null;

      if (this._tableControl.isChartVisible()) {
        // We give the chart time to finish rendering.
        // TODO: Can we programmatically determine if animations are complete.
        await Solovis.Async.delay(2000);

        let chartRow = this._nextRow++;
        let chartRowObj = this._ws.getRow(chartRow);

        let svg = $("svg", this._tableControl._chartContainer);
        let svgStr = new XMLSerializer().serializeToString(svg.get(0));
        let svgImgSrc = "data:image/svg+xml;utf8," + encodeURIComponent(svgStr);

        let imgWidth = $(svg).width();
        let imgHeight = $(svg).height();

        let canvas = document.createElement('canvas');
        canvas.width = imgWidth;
        canvas.height = imgHeight;

        let canvasContext = canvas.getContext('2d');

        let img = new Image();
        img.src = svgImgSrc;

        // NOTE: This seemed to be necessary to give Image a chance to incorporate the image src.
        await Solovis.Async.delay(1000);

        canvasContext.drawImage(img, 0, 0);

        await Solovis.Async.delay(10);

        let imageDataUrl = canvas.toDataURL("image/png");

        // This will add the image to the workbook and then create an instance of rendering the image in cell A1.                
        let imageId = this._excelWorkbook.addImage({
          base64: imageDataUrl,
          extension: "png"
        });

        imageToAdd = {
          imageId: imageId,
          width: imgWidth,
          height: imgHeight
        };
        this._imageToAdd = imageToAdd;

        chartRowObj.height = imgHeight / pixelToExcelHeightRatio;
      }

      if (!this._tableControl.isTableHidden()) {
        // This will render current view of DataTableControl to specified sheet name in Excel workbook.
        let columns = this._dataView.visibleColumns;
        let columnCount = columns ? columns.length : 0;

        // Determine aggregateDisplay and group header settings.
        let tableSettingsToUse = this._tableSettingsToUse;
        let aggregateDisplay = tableSettingsToUse.aggregateRowDisplay;

        let groupHeaderColspan = columnCount;
        if (aggregateDisplay === "inline") {
          let desiredColspan = 1;
          if (tableSettingsToUse.groupHeaderColspan) {
            desiredColspan = tableSettingsToUse.groupHeaderColspan;
          }

          groupHeaderColspan = Math.min(desiredColspan, columnCount);
        }

        this._appendHeaders(columns);

        let solovisTableRenderingData = {
          columns: columns,
          groupHeaderColspan: groupHeaderColspan,
          aggregateDisplay: aggregateDisplay
        };

        for (var i = 0; i < this._dataView.flattenedRows.length; i++) {
          let flattenedRow = this._dataView.flattenedRows[i];
          this._appendRow(flattenedRow, solovisTableRenderingData, i);
        }
      }

      if (imageToAdd) {
        // Can we create floating image box? How do we get this sized with accurate aspect ratio.
        this._ws.addImage(imageToAdd.imageId, {
          tl: { col: 0, row: 0 },
          br: { col: (imageToAdd.rightColumnIndex ? imageToAdd.rightColumnIndex : 1), row: 1 },
          editAs: "absolute"
        });
      }
    }

    private _appendHeaders(columns) {
      let self = this;

      // Check for super headers.
      let maxSuperHeaders = 0;
      $(columns).each(function (i, col: any) {
        if (col.superHeaders) {
          maxSuperHeaders = Math.max(maxSuperHeaders, col.superHeaders.length);
        }
      });

      for (var superHeaderIndex = maxSuperHeaders - 1; superHeaderIndex >= 0; superHeaderIndex--) {
        let excelSuperHeaderRowIndex = this._nextRow++;

        var superHeaderLabels = [];
        $(columns).each(function (i, col: any) {
          if (col.superHeaders && col.superHeaders.length > superHeaderIndex) {
            superHeaderLabels.push(col.superHeaders[superHeaderIndex]);
          }
          else {
            superHeaderLabels.push(null);
          }
        });

        $(columns).each(function (i, col) {
          let superHeaderLabel = superHeaderLabels[i];

          if (i == 0 || (superHeaderLabels[i - 1] != superHeaderLabel)) {
            let excelSuperHeaderColIndex = i + 1;
            let excelSuperHeaderCell = self._ws.getRow(excelSuperHeaderRowIndex).getCell(excelSuperHeaderColIndex);

            // Determine colspan by consecutive headers with same label.
            var colspan = 1;
            while (i + colspan < superHeaderLabels.length) {
              if (superHeaderLabels[i + colspan] != superHeaderLabel) {
                break;
              }

              colspan++;
            }

            // Merge cells of superheader span.
            if (colspan > 1) {
              self._ws.mergeCells(excelSuperHeaderRowIndex, excelSuperHeaderColIndex, excelSuperHeaderRowIndex, excelSuperHeaderColIndex + (colspan - 1));
            }

            if (superHeaderLabel !== null) {
              excelSuperHeaderCell.value = superHeaderLabels[i];
            }

            excelSuperHeaderCell.border = {
              left: { style: "thin" },
              right: { style: "thin" },
              color: { argb: standardExcelTableBorderColor }
            };

            excelSuperHeaderCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          }
        });
      }

      // Regular headers.
      let excelHeaderRowIndex = this._nextRow++;
      let headerRowObj = this._ws.getRow(excelHeaderRowIndex);

      let totalColWidthInPixelsSoFar = 0;

      if (columns) {
        for (let i = 0; i < columns.length; i++) {
          let col = columns[i];
          let colObj = this._tableControl.columnSet.getColumnById(col.id);

          let excelColIndex = i + 1;

          let headerCell = headerRowObj.getCell(excelColIndex);

          headerCell.value = col.label;

          let currentColWidthInPixels;

          if (col.width) {
            this._ws.getColumn(excelColIndex).width = col.width / pixelToExcelWidthRatio;
            currentColWidthInPixels = col.width;
          }
          else {
            currentColWidthInPixels = defaultExcelColumnWidthInPixels;
          }

          totalColWidthInPixelsSoFar += currentColWidthInPixels;
          if (this._imageToAdd && !this._imageToAdd.rightColumnIndex) {
            if (totalColWidthInPixelsSoFar > this._imageToAdd.width) {
              let cellCoverageRatio = 1.0 - ((totalColWidthInPixelsSoFar - this._imageToAdd.width) / currentColWidthInPixels);
              this._imageToAdd.rightColumnIndex = i + cellCoverageRatio;
            }
          }

          applyStandardCellBorder(headerCell);
          applyStandardHeaderBgColor(headerCell);

          headerCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        }
      }

      if (this._imageToAdd && !this._imageToAdd.rightColumnIndex) {
        // We never found the right edge.
        let excessPixels = this._imageToAdd.width - totalColWidthInPixelsSoFar;
        let excessColumns = excessPixels / defaultExcelColumnWidthInPixels;

        this._imageToAdd.rightColumnIndex = columns.length + excessColumns;

        // We merge the cells behind the image.
        this._ws.mergeCells(1, 1, 1, Math.ceil(this._imageToAdd.rightColumnIndex));
      }
    }


    private _appendRow(rowData, solovisTableRendering, flattenedRowIndex) {
      let self = this;

      let columns = solovisTableRendering.columns;
      let tableControl = this._tableControl;

      let row = rowData.row;
      let level = rowData.level;

      if (rowData.isGroupHeader) {
        this._appendGroupHeaderRow(rowData, solovisTableRendering, flattenedRowIndex);
      }
      else if (rowData.isAggregateRow) {
        this._appendAggregateRow(rowData, solovisTableRendering);
      }
      else {
        // Regular row.  

        let excelRowIndex = this._nextRow++;
        let excelRow = this._ws.getRow(excelRowIndex);

        // This is a regular row.
        if (columns) {
          $(columns).each(function (colIndex, col: any) {
            let cellValue = tableControl._getColumnValueForRow(col, row);

            let excelCell = excelRow.getCell(colIndex + 1);

            if (!self._trySetSpecialCellValue(excelCell, col, cellValue)) {
              // We were not able to use the explicit rules for formatting known value types in Excel, so use text version of html.
              let cellContext = {
                reportControl: tableControl.reportControl,
                dataTable: tableControl,
                row: row,
                level: level,
                modelEnvironment: tableControl.modelEnvironment,
                col: col,
                colIndex: colIndex,
                defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml,
                forExport: true
              };

              let cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);
              internalSetExcelCellFromHtmlIfNonNull(excelCell, cellValueHtml);
            }

            // When row is grouped the first column should be indented.
            if (colIndex == 0 && level > 0) {
              applyExcelCellIndent(excelCell, level);
            }
            else {
              if (col.textAlign === "center") {
                excelCell.alignment = { horizontal: "center" };
              }
            }

            applyStandardBgColor(excelCell, (flattenedRowIndex % 2) == 1);
            applyStandardCellBorder(excelCell);
          });
        }
      }
    }

    private _appendGroupHeaderRow(rowData, solovisTableRendering, flattenedRowIndex) {
      let self = this;

      let columns = solovisTableRendering.columns;
      let tableControl = this._tableControl;

      let row = rowData.row;
      let level = rowData.level;

      let groupHeaderColspan = solovisTableRendering.groupHeaderColspan;
      let displayAggregatesInline = rowData.inlineAggregates;

      if (row._subRowReplacement) {
        // We always use group header colspan of 1 and displayAggregates inline on sub-row replacements.
        groupHeaderColspan = 1;
        displayAggregatesInline = true;
      }

      let excelRowIndex = this._nextRow++;
      let excelRow = this._ws.getRow(excelRowIndex);

      let groupHeaderCell = excelRow.getCell(1);

      // Merge cells across group header colspan.
      if (groupHeaderColspan > 1) {
        this._ws.mergeCells(excelRowIndex, 1, excelRowIndex, groupHeaderColspan);
      }

      if (level > 0) {
        applyExcelCellIndent(groupHeaderCell, level);
      }

      applyStandardBgColor(groupHeaderCell, (flattenedRowIndex % 2) == 1);
      applyStandardCellBorder(groupHeaderCell);

      // This is a group header row.
      let group = tableControl.groupManager.getGroupById(row._groupId);
      if (!group) {
        if (row._subRowReplacement) {
          let col = columns[0];
          // We'll use the normal way of rendering first row for sub-row replacement rows.
          let cellValue = tableControl._getColumnValueForRow(col, row._backingRow);

          let cellContext = {
            reportControl: tableControl.reportControl,
            dataTable: tableControl,
            row: row._backingRow,
            level: level,
            modelEnvironment: tableControl.modelEnvironment,
            col: col,
            defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml
          };

          var cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);
          internalSetExcelCellFromHtmlIfNonNull(groupHeaderCell, cellValueHtml);
        }
        else {
          groupHeaderCell.value = "Group: " + row._groupId;
        }
      }
      else {
        let cellContext = {
          reportControl: tableControl.reportControl,
          dataTable: tableControl,
          row: row,
          level: level,
          modelEnvironment: tableControl.modelEnvironment,
          group: group,
          defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml
        };

        var groupRep = row._groupRep;
        var groupHeaderHtml = tableControl._formatCellHtml(groupRep, cellContext);
        if (groupHeaderHtml === null || groupHeaderHtml === "")
          groupHeaderHtml = "[Unknown]";

        internalSetExcelCellFromHtmlIfNonNull(groupHeaderCell, groupHeaderHtml);
      }

      let cellValueGetter = function (row, col) {
        if (row._backingRow) {
          // If column has explicit aggregation, we should use that over locally aggregated value.
          let colObject = tableControl.columnSet.getColumnById(col.id);
          if (colObject && colObject.mods && colObject.mods.aggregation) {
            let cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
            return cellValue;
          }
          else {
            let cellValue = tableControl._getColumnValueForRow(col, row._backingRow);
            return cellValue;
          }
        }
        else {
          let cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
          return cellValue;
        }
      };


      if (displayAggregatesInline) {
        if (columns) {
          $(columns).each(function (i, col: any) {
            // We will only see aggregates not covered by group header label.
            if (i >= groupHeaderColspan) {
              let id = col.id;
              let cellValue = cellValueGetter(row, col);

              let cell = excelRow.getCell(i + 1);

              if (!self._trySetSpecialCellValue(cell, col, cellValue, true)) {
                let cellContext = {
                  reportControl: tableControl.reportControl,
                  dataTable: tableControl,
                  row: row,
                  level: level,
                  modelEnvironment: tableControl.modelEnvironment,
                  col: col,
                  defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml,
                  aggregate: true
                };

                let cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);
                internalSetExcelCellFromHtmlIfNonNull(cell, cellValueHtml);
              }

              if (col.textAlign === "center") {
                cell.alignment = { horizontal: "center" };
              }

              applyStandardBgColor(cell, (flattenedRowIndex % 2) == 1);
              applyStandardCellBorder(cell);
            }
          });
        }
      }
    }

    private _appendAggregateRow(rowData, solovisTableRendering) {
      let self = this;

      let columns = solovisTableRendering.columns;
      let tableControl = this._tableControl;

      let row = rowData.row;
      let level = rowData.level;

      let excelRowIndex = this._nextRow++;
      let excelRow = this._ws.getRow(excelRowIndex);

      if (row._groupTotals || row._backingRow) {
        var cellValueGetter;
        if (row._backingRow) {
          cellValueGetter = function (row, col) {
            var cellValue = tableControl._getColumnValueForRow(col, row._backingRow);
            return cellValue;
          };
        }
        else {
          cellValueGetter = function (row, col) {
            var cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
            return cellValue;
          };
        }

        if (columns) {
          $(columns).each(function (i, col: any) {
            let id = col.id;
            let cellValue = cellValueGetter(row, col);

            let cell = excelRow.getCell(i + 1);

            if (!self._trySetSpecialCellValue(cell, col, cellValue, true)) {
              let cellContext = {
                reportControl: tableControl.reportControl,
                dataTable: tableControl,
                row: row,
                level: level,
                modelEnvironment: tableControl.modelEnvironment,
                col: col,
                defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml,
                aggregate: true
              };

              let cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);
              internalSetExcelCellFromHtmlIfNonNull(cell, cellValueHtml);
            }

            if (col.textAlign === "center") {
              cell.alignment = { horizontal: "center" };
            }

            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: standardExcelTableAggregateBgColor },
              bgColor: { argb: standardExcelTableAggregateBgColor }
            };

            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
              color: { argb: standardExcelTableBorderColor }
            };
          });
        }
      }
    }


    /**
     * If it's possible to set specially formatted/typed cell value, then do so and return true;
     * otherwise, return false to indicate that text version of html should be used.
     */
    private _trySetSpecialCellValue(excelCell, col, cellValue, isGroupOrAggRow?: boolean): boolean {
      let dataTypeToUse = col.dataType;

      if (col.aggregation && col.aggregation.aggregator) {
        // For aggregation types that convert the data type, we must update the aggregator to use.
        switch (col.aggregation.aggregator) {
          case "count":
          case "countIfNotNull":
          case "uniqueCount":
          case "uniqueCountIfNotNull":
            dataTypeToUse = "integer";
            break;
        }
      }

      if (dataTypeToUse === "dayId") {
        Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(excelCell, cellValue);

        excelCell.numFmt = "MMM D, YYYY";

        return true;
      }
      else if (dataTypeToUse === "percentage" || dataTypeToUse === "currency" || dataTypeToUse === "number") {
        excelCell.value = cellValue;

        excelCell.numFmt = this._getExcelNumFmtForNumericCol(col, dataTypeToUse);

        return true;
      }
      else if (dataTypeToUse === "hybridReturn") {
        excelCell.value = cellValue?.value;
        excelCell.numFmt = this._getExcelNumFmtForNumericCol(col, dataTypeToUse);
        return true;
      }
      else if (col.dataType === "integer") {
        excelCell.value = cellValue;

        excelCell.numFmt = this._getExcelNumFmtForNumericCol(col, dataTypeToUse);

        return true;
      }
      else if (col.dataType === "string") {
        excelCell.value = cellValue;

        return true;
      }

      return false;
    }

    private _getExcelNumFmtForNumericCol(col, dataTypeOverride?: string) {
      let dataTypeToUse = dataTypeOverride ? dataTypeOverride : col.dataType;

      if (dataTypeToUse === "percentage" || dataTypeToUse === "currency" || dataTypeToUse === "number" || dataTypeToUse === "hybridReturn") {
        let numFmt = "#,##0";

        let scale = 2;
        if (col.formatHint && Solovis.isNotNull(col.formatHint.scale)) {
          scale = col.formatHint.scale;
        }

        if (scale > 0) {
          numFmt += ".";
          for (let i = 0; i < scale; i++) {
            numFmt += "0";
          }
        }

        if (dataTypeToUse === "percentage" || dataTypeToUse === "hybridReturn") {
          numFmt += "%";
        }

        return numFmt;
      }
      if (dataTypeToUse === "integer") {
        return "0";
      }
      else {
        return null;
      }
    }
  }

}